/**
 * FrankOs cart clear v1.3
 *
 * Clears every cart line.
 *
 * v1.3:
 * - Als KleurWissel-punten actief/gereserveerd zijn, worden die eerst via
 *   de bestaande centrale KleurWissel-release vrijgegeven.
 * - Pas na een geslaagde vrijgave wordt cart/clear.js uitgevoerd.
 * - Hiermee kan een lege winkelwagen nooit 5.000 (of minder) punten
 *   onzichtbaar gereserveerd achterlaten.
 *
 * Drawer behaviour:
 * - uses Horizon's existing #empty-cart-template after a successful clear;
 * - keeps the cart drawer open;
 * - shows the normal empty-cart state immediately;
 * - resets header cart count/status without reloading the product page.
 *
 * Full cart page:
 * - keeps the existing reload fallback because the drawer empty template
 *   is not present on the dedicated cart page.
 */
class FrankOsCartClear extends HTMLElement {
  connectedCallback() {
    if (this.dataset.frankosBound === 'true') return;

    this.dataset.frankosBound = 'true';
    this.button = this.querySelector('[data-frankos-cart-clear-button]');
    this.status = this.querySelector('[data-frankos-cart-clear-status]');

    this.button?.addEventListener('click', this.handleClear);
  }

  disconnectedCallback() {
    this.button?.removeEventListener('click', this.handleClear);
  }

  handleClear = async () => {
    if (!(this.button instanceof HTMLButtonElement)) return;

    const confirmed = window.confirm(
      'Weet je zeker dat je alle producten uit de winkelwagen wilt verwijderen?'
    );

    if (!confirmed) return;

    this.button.disabled = true;
    this.button.setAttribute('aria-busy', 'true');

    if (this.status) {
      this.status.textContent = 'Winkelwagen wordt leeggemaakt…';
    }

    try {
      /*
       * Als er een actieve KleurWissel zichtbaar is, geef de reservering
       * EERST vrij. De centrale helper staat in frankos-kleurwissel-cart.js,
       * dat voor ingelogde klanten globaal vanuit theme.liquid wordt geladen.
       *
       * Bij een releasefout wordt de winkelwagen bewust niet leeggemaakt:
       * zo kunnen Shopify-cart en KleurPunten nooit uit elkaar lopen.
       */
      const kleurWissel = document.querySelector('frankos-kleurwissel-cart');
      const reservedPoints = Number.parseInt(
        String(kleurWissel?.dataset?.reservedPoints || '0'),
        10
      ) || 0;
      const appliedPoints = Number.parseInt(
        String(kleurWissel?.dataset?.appliedPoints || '0'),
        10
      ) || 0;

      if (
        (reservedPoints > 0 || appliedPoints > 0)
        && typeof window.frankosReleaseKleurWisselReservation === 'function'
      ) {
        if (this.status) {
          this.status.textContent = 'KleurWissel wordt vrijgegeven…';
        }

        await window.frankosReleaseKleurWisselReservation();
      }

      const root = window.Shopify?.routes?.root || '/';
      const response = await fetch(`${root}cart/clear.js`, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Cart clear failed: ${response.status}`);
      }

      const clearedCart = await response.json();

      /*
       * Remove Horizon's short-lived cart-count cache so an old count
       * cannot be restored after the cart has already been cleared.
       */
      sessionStorage.removeItem('cart-count');

      /*
       * Keep FrankOs' shared cart snapshot aligned with Shopify's
       * confirmed empty cart and synchronise every product-tile control.
       */
      window.frankosInitialCartPromise = Promise.resolve(clearedCart);
      document.dispatchEvent(
        new CustomEvent('cart:updated', {
          bubbles: true,
          detail: { resource: clearedCart },
        })
      );

      /*
       * Reset the visible header cart state immediately.
       * This mirrors the zero-item state that cart-icon.js normally reaches
       * through Horizon's cart update flow.
       */
      document.querySelectorAll('cart-icon').forEach((cartIcon) => {
        cartIcon.classList.remove(
          'header-actions__cart-icon--has-cart',
          'frankos-cart-filled'
        );

        const bubble = cartIcon.querySelector('.cart-bubble');
        const bubbleCount = cartIcon.querySelector('.cart-bubble__text-count');

        bubble?.classList.add('visually-hidden');
        bubbleCount?.classList.add('hidden');

        if (bubbleCount) {
          bubbleCount.textContent = '0';
        }
      });

      document.body.classList.remove('frankos-cart-has-items');

      /*
       * In the cart drawer, use Horizon's own empty-cart template instead
       * of reloading the whole page. This keeps the drawer open and shows
       * the same "cart is empty" state as removing the final line normally.
       */
      const emptyTemplate = document.getElementById('empty-cart-template');
      const drawerCartItems = document.querySelector(
        'cart-items-component[data-drawer]'
      );

      if (
        emptyTemplate instanceof HTMLTemplateElement &&
        drawerCartItems instanceof HTMLElement
      ) {
        const clone = document.importNode(emptyTemplate.content, true);
        drawerCartItems.replaceChildren(clone);

        const drawerDialog = drawerCartItems.closest('.cart-drawer__dialog');
        drawerDialog?.classList.add('cart-drawer--empty');

        return;
      }

      /*
       * Dedicated cart page fallback: there is no drawer empty template there,
       * so preserve the previous refresh behaviour.
       */
      window.location.reload();
    } catch (error) {
      console.error('[FrankOs] Winkelwagen leegmaken is mislukt.', error);

      this.button.disabled = false;
      this.button.removeAttribute('aria-busy');

      if (this.status) {
        this.status.textContent =
          'Leegmaken is mislukt. Probeer het opnieuw.';
      }
    }
  };
}

if (!customElements.get('frankos-cart-clear')) {
  customElements.define('frankos-cart-clear', FrankOsCartClear);
}
